import { useEffect, useState } from "react";

const offerCards = [
  {
    title: "Expert Talks",
    desc: "Hear directly from top NEET counsellors and doctors on cracking admissions.",
    image: "/images/Expert_talks.webp",
  },
  {
    title: "Live Q&A",
    desc: "Ask your toughest NEET UG doubts live — college selection, cutoffs & more.",
    image: "/images/liveqa.webp",
  },
  {
    title: "1-on-1 Guidance",
    desc: "Get personalised college & branch selection advice from NEET experts.",
    image: "/images/1-on-1.webp",
  },
];

const stats = [
  { value: "10+", label: "Expert Speakers" },
  { value: "500+", label: "Seats Available" },
  { value: "6+", label: "Sessions" },
  { value: "1", label: "Day Summit" },
];

const DEFAULT_CONFIG = {
  date: "28 June 2026",
  time: "11:00 AM IST",
  venueShort: "Vishwa Yuvak Kendra, Chanakyapuri, New Delhi",
  address:
    "Pt. Uma Shankar Dikshit Road, Teen Murti Marg, Circular Road, Chanakyapuri, New Delhi - 110021",
  mapsUrl: "https://maps.app.goo.gl/kbCtfehR2MAnsTK59",
  registrationOpen: true,
};

export default function OfferSection({ onRegister }) {
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    const baseUrl = process.env.NEXT_PUBLIC_ADMIN_URL || "";

    const fetchSeminarConfig = async () => {
      try {
        const response = await fetch(
          `${baseUrl}/api/seminar-config/neet-ug-2026-summit`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            cache: "no-store",
          }
        );

        if (!response.ok) {
          return;
        }

        const json = await response.json();

        if (json?.data && typeof json.data === "object") {
          setCfg({
            ...DEFAULT_CONFIG,
            ...json.data,
          });
        }
      } catch (error) {
        console.warn("Seminar config not reachable. Using default config.");
      }
    };

    fetchSeminarConfig();
  }, []);

  const handleRegisterClick = () => {
    if (cfg.registrationOpen === false) {
      return;
    }

    if (typeof onRegister === "function") {
      onRegister();
    }
  };

  const isRegistrationOpen = cfg.registrationOpen !== false;

  return (
    <section className="relative overflow-hidden px-5 pb-16 pt-20 sm:px-8 lg:px-14 lg:pb-24 lg:pt-28">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <header className="relative text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-center capitalize">
            What We <span className="text-[#ff9245]">Offer?</span>
          </h2>

          <p className="mx-auto mt-3 w-full text-sm sm:text-lg text-slate-600">
            Full-day NEET UG 2026 summit for clear strategy, expert guidance,
            and better college admissions.
          </p>
        </header>

        {/* Cards */}
        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {offerCards.map((card) => (
            <article
              key={card.title}
              className="group relative h-[290px] overflow-hidden rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.10)]"
            >
              <img
                src={card.image}
                alt={card.title}
                loading="lazy"
                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              />

              <div className="absolute inset-0 bg-zinc-900/40 transition group-hover:bg-zinc-900/55" />

              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
                <h3 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  {card.title}
                </h3>

                <p className="mt-3 text-sm leading-6 text-white/85 opacity-0 translate-y-3 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                  {card.desc}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-14 grid grid-cols-2 gap-6 sm:grid-cols-4">
          {stats.map((item) => (
            <div key={item.label} className="text-center">
              <div className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-orange-50 border-2 border-orange-100 sm:h-32 sm:w-32 lg:h-36 lg:w-36">
                <span className="text-3xl font-bold leading-none tracking-tight text-[#ff9245] sm:text-4xl lg:text-5xl">
                  {item.value}
                </span>
              </div>

              <p className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-zinc-500 sm:text-xs">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 flex flex-col items-center gap-4 text-center">
          {/* Address Card - always visible */}
          <a
            href={cfg.mapsUrl || DEFAULT_CONFIG.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`group flex w-full max-w-xl items-start gap-4 rounded-2xl p-4 shadow-sm transition-all duration-300 sm:p-5 ${
              isRegistrationOpen
                ? "border border-orange-100 bg-white hover:-translate-y-0.5 hover:border-[#ff9245]/60 hover:shadow-lg"
                : "border border-zinc-200 bg-zinc-50"
            }`}
          >
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl transition-colors ${
                isRegistrationOpen
                  ? "bg-[#fff3ea] text-[#ff9245] group-hover:bg-[#ff9245] group-hover:text-white"
                  : "bg-zinc-200 text-zinc-600"
              }`}
            >
              {isRegistrationOpen ? "📍" : "⏳"}
            </span>

            <div className="min-w-0 flex-1 text-left">
              {!isRegistrationOpen && (
                <span className="mb-2 inline-flex rounded-full bg-zinc-200 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                  Past Event
                </span>
              )}

              <p
                className={`text-sm font-bold leading-5 sm:text-base ${
                  isRegistrationOpen
                    ? "text-zinc-900 transition-colors group-hover:text-[#ff9245]"
                    : "text-zinc-900"
                }`}
              >
                {cfg.venueShort || DEFAULT_CONFIG.venueShort}
              </p>

              <p className="mt-1 text-xs leading-5 text-zinc-500 sm:text-sm">
                {cfg.address || DEFAULT_CONFIG.address}
              </p>

              <p
                className={`mt-3 inline-flex items-center gap-1.5 text-xs font-bold sm:text-sm ${
                  isRegistrationOpen ? "text-[#ff9245]" : "text-zinc-500"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4 shrink-0"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-2.003 3.5-4.697 3.5-8.327a8.25 8.25 0 00-16.5 0c0 3.63 1.556 6.324 3.5 8.327a19.58 19.58 0 002.683 2.282 16.975 16.975 0 001.144.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
                    clipRule="evenodd"
                  />
                </svg>

                View on Google Maps

                <span className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </p>

              {!isRegistrationOpen && (
                <p className="mt-3 text-xs leading-5 text-zinc-500 sm:text-sm">
                  This seminar has already been completed. Registrations are now
                  closed for this event.
                </p>
              )}
            </div>
          </a>

          <p className="text-sm font-semibold uppercase tracking-widest text-zinc-400">
            {cfg.date || DEFAULT_CONFIG.date} &nbsp;·&nbsp;{" "}
            {cfg.time || DEFAULT_CONFIG.time}
          </p>

          <button
            type="button"
            onClick={handleRegisterClick}
            disabled={!isRegistrationOpen}
            className={`mt-1 inline-block rounded-full px-10 py-4 text-sm font-bold uppercase tracking-wide text-white transition ${
              isRegistrationOpen
                ? "bg-[#ff9245] shadow-[0_12px_30px_rgba(255,146,69,0.35)] hover:-translate-y-0.5 hover:bg-[#ff9245]/90 cursor-pointer"
                : "bg-zinc-400 cursor-not-allowed opacity-60"
            }`}
          >
            {isRegistrationOpen ? "Reserve Your Seat" : "Registrations Closed"}
          </button>
        </div>
      </div>
    </section>
  );
}